import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { CachedEmployeePatterns, LeaveListItem, LeaveTypeId } from '@lms/shared-types'
import { Avatar } from '../primitives/Avatar'
import { Button } from '../primitives/Button'
import { Icon } from '../primitives/Icon'
import { LeaveTypePill } from '../primitives/LeaveTypePill'
import { Severity } from '../primitives/Severity'
import { decideLeave } from '../../api/ai-panel-regenerate'
import {
  RevocationWindowClosedError,
  rejectLeave,
  revokeApproveDecision,
} from '../../api/leaves'
import { getCachedTeamPatterns } from '../../api/patterns'
import { displayName } from '../../lib/display-name'
import { fmtDateRange } from '../../lib/format'
import { flags } from '../../lib/feature-flags'
import type { RowBadge } from '../../lib/manager-queue'
import { useToast } from '../primitives/Toast'
import { EmployeePatternsModal } from './EmployeePatternsModal'
import { ConversationDraftModal } from './ConversationDraftModal'

export interface ManagerQueueRowProps {
  leave: LeaveListItem
  /**
   * Pre-resolved single badge from pickTopBadge. The card decides what
   * shows up here; the row never picks between signals on its own.
   */
  badge: RowBadge | null
  leaveTypeMeta: Record<LeaveTypeId, { label: string; color: string }>
  /** Called after a successful decision so the parent can refetch / remove the row. */
  onDecided: () => void
}

/**
 * Single pending-row, designed for Hafiz's "decide in five seconds" flow.
 *
 * - Approve: zero-friction one-click via /decide
 * - ⋯ kebab: Reject (popover) + Open
 * - One badge per row, sourced via pickTopBadge — Maya's loud-when-loud
 *   rule. The detail page carries the longer story.
 */
export function ManagerQueueRow({
  leave,
  badge,
  leaveTypeMeta,
  onDecided,
}: ManagerQueueRowProps): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const undoEnabled = flags.managerApproveUndo
  const [inFlight, setInFlight] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [patternsOpen, setPatternsOpen] = useState(false)
  const [draftOpen, setDraftOpen] = useState(false)

  const kebabRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const meta = leaveTypeMeta[leave.type]
  const name = displayName(leave.employee)
  const range = fmtDateRange(leave.startDate, leave.endDate)

  // Patterns data — shared cache with ManagerQueueCard. We fetch lazily
  // (enabled only when the drawer opens) so the row is free of overhead
  // until the manager actually requests it.
  const patternsQuery = useQuery({
    queryKey: ['patterns', 'team', 'manager-queue'],
    queryFn: () => getCachedTeamPatterns(),
    staleTime: 5 * 60 * 1000,
    enabled: patternsOpen,
  })

  const employeePatternData: CachedEmployeePatterns =
    patternsQuery.data?.items.find((it) => it.employeeId === leave.employee.id) ?? {
      employeeId: leave.employee.id,
      employeeName: name,
      teamName: leave.team.name,
      departmentName: '',
      patterns: [],
      detectedAt: null,
      annualLeft: null,
      annualTotal: null,
      patternMetrics: {},
      replacementDays: 0,
      replacementExpiresAt: null,
    }

  function refreshAfterDecision(): void {
    void queryClient.invalidateQueries({ queryKey: ['requests'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    onDecided()
  }

  function handleUndo(): void {
    void revokeApproveDecision(leave.id)
      .then(() => {
        notify(`Reversed ${name}'s ${leave.type} leave.`, 'info')
        refreshAfterDecision()
      })
      .catch((err: unknown) => {
        if (err instanceof RevocationWindowClosedError) {
          notify(
            'Window closed — open the leave to reverse manually.',
            'warning',
            {
              label: 'Open',
              onClick: () =>
                void navigate({ to: '/leave', search: { id: leave.id } as never }),
            },
          )
          return
        }
        notify(
          err instanceof Error ? err.message : 'Could not undo approval',
          'error',
        )
      })
  }

  function handleApprove(): void {
    setInFlight(true)
    setError(null)
    void decideLeave(leave.id, { decision: 'approve' })
      .then(() => {
        if (undoEnabled) {
          // 60 s window matches the server's REVOKE_APPROVE_WINDOW_SECONDS.
          // Auto-dismiss is set to the same so the affordance vanishes when
          // the actual window closes — Hafiz's "I switched tabs" case.
          notify(
            `Approved ${name}'s ${leave.type} leave.`,
            'success',
            { label: 'Undo', onClick: handleUndo },
            { durationMs: 60_000 },
          )
        }
        refreshAfterDecision()
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Approve failed')
      })
      .finally(() => {
        setInFlight(false)
      })
  }

  function handleReject(): void {
    const reason = rejectReason.trim()
    if (reason.length < 4) {
      setError('Add a short reason so the employee knows why.')
      return
    }
    setInFlight(true)
    setError(null)
    void rejectLeave(leave.id, reason)
      .then(() => {
        closeReject()
        refreshAfterDecision()
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Reject failed')
      })
      .finally(() => {
        setInFlight(false)
      })
  }

  const closeMenu = useCallback((): void => setMenuOpen(false), [])

  function openRejectFromMenu(): void {
    setMenuOpen(false)
    setRejectOpen(true)
    setError(null)
  }

  function openDetailFromMenu(): void {
    setMenuOpen(false)
    void navigate({ to: '/leave', search: { id: leave.id } as never })
  }

  function openPatternsFromMenu(): void {
    setMenuOpen(false)
    setPatternsOpen(true)
  }

  function closeReject(): void {
    setRejectOpen(false)
    setRejectReason('')
    setError(null)
  }

  // ── outside-click close for menu + popover ──────────────────────────────────
  useEffect(() => {
    if (!menuOpen && !rejectOpen) return
    function handleClick(e: MouseEvent): void {
      const target = e.target as Node
      const inKebab = kebabRef.current?.contains(target) ?? false
      const inMenu = menuRef.current?.contains(target) ?? false
      const inPopover = popoverRef.current?.contains(target) ?? false
      if (!inKebab && !inMenu && !inPopover) {
        setMenuOpen(false)
        // Don't auto-close the reject popover — a stray click shouldn't
        // discard a half-typed reason. Escape and Cancel are the only
        // ways out.
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen, rejectOpen])

  const handleMenuKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        closeMenu()
        kebabRef.current?.focus()
      }
    },
    [closeMenu],
  )

  const handlePopoverKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      closeReject()
      kebabRef.current?.focus()
    }
  }, [])

  return (
    <li
      data-testid="queue-row"
      data-leave-id={leave.id}
      style={{
        padding: '12px 0',
        borderBottom: '1px solid var(--french-gray-2)',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '28px 1fr auto',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <Avatar name={name} size={28} />

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <Link
            to="/leave"
            search={{ id: leave.id } as never}
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}
            data-testid="queue-row-name"
          >
            {name}
          </Link>
          {meta ? (
            <LeaveTypePill
              leaveTypeId={leave.type}
              label={meta.label}
              color={meta.color}
              size="sm"
            />
          ) : null}
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{range}</span>
          {badge ? (
            <Severity kind={badge.severity}>
              <span data-testid="queue-row-badge" data-badge-kind={badge.kind}>
                {badge.label}
              </span>
            </Severity>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative' }}>
          <Button
            variant="primary"
            size="sm"
            data-testid="queue-row-approve"
            disabled={inFlight}
            onClick={handleApprove}
          >
            Approve
          </Button>
          <button
            ref={kebabRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`More actions for ${name}'s request`}
            data-testid="queue-row-more"
            disabled={inFlight}
            onClick={() => setMenuOpen((prev) => !prev)}
            style={{
              minWidth: 32,
              minHeight: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid transparent',
              borderRadius: 6,
              background: menuOpen ? 'var(--surface-2)' : 'transparent',
              color: 'var(--text-2)',
              cursor: 'pointer',
              transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--surface-2)'
              e.currentTarget.style.borderColor = 'var(--border)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = menuOpen ? 'var(--surface-2)' : 'transparent'
              e.currentTarget.style.borderColor = menuOpen ? 'var(--border)' : 'transparent'
            }}
          >
            <Icon name="more_horiz" size={16} aria-hidden />
          </button>

          {menuOpen ? (
            <div
              ref={menuRef}
              role="menu"
              data-testid="queue-row-menu"
              onKeyDown={handleMenuKeyDown}
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                minWidth: 140,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: 'var(--shadow-2)',
                zIndex: 20,
                overflow: 'hidden',
              }}
            >
              <MenuButton
                label="Reject"
                testId="queue-row-menu-reject"
                onClick={openRejectFromMenu}
              />
              <MenuButton
                label="Open"
                testId="queue-row-menu-open"
                onClick={openDetailFromMenu}
              />
              <MenuButton
                label="View patterns"
                testId="queue-row-menu-patterns"
                onClick={openPatternsFromMenu}
              />
            </div>
          ) : null}

          {rejectOpen ? (
            <div
              ref={popoverRef}
              data-testid="queue-row-reject-popover"
              onKeyDown={handlePopoverKeyDown}
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                width: 320,
                padding: 12,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: 'var(--shadow-2)',
                zIndex: 25,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason (visible to employee) — short is fine"
                rows={3}
                autoFocus
                data-testid="queue-row-reject-reason"
                style={{
                  fontSize: 13,
                  fontFamily: 'inherit',
                  padding: '8px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  resize: 'vertical',
                }}
              />
              {error ? (
                <div role="alert" style={{ fontSize: 12, color: 'var(--danger-fg)' }}>
                  {error}
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={inFlight}
                  onClick={closeReject}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  data-testid="queue-row-reject-confirm"
                  disabled={inFlight}
                  onClick={handleReject}
                >
                  Confirm reject
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {error && !rejectOpen ? (
        <div
          role="alert"
          style={{
            fontSize: 12,
            color: 'var(--danger-fg)',
            paddingLeft: 40,
            marginTop: 6,
          }}
        >
          {error}
        </div>
      ) : null}

      {patternsOpen ? (
        <EmployeePatternsModal
          open={patternsOpen}
          onClose={() => { setPatternsOpen(false) }}
          employeeId={leave.employee.id}
          employeeName={name}
          teamName={leave.team.name}
          data={employeePatternData}
          onDraftConversation={() => {
            setPatternsOpen(false)
            setDraftOpen(true)
          }}
        />
      ) : null}

      {flags.aiConversationDrafter && draftOpen ? (
        <ConversationDraftModal
          open={draftOpen}
          onClose={() => setDraftOpen(false)}
          employeeId={leave.employee.id}
          employeeName={name}
          patternKinds={employeePatternData?.patterns.map((p) => p.kind) ?? []}
        />
      ) : null}
    </li>
  )
}

interface MenuButtonProps {
  label: string
  onClick: () => void
  testId?: string
}

function MenuButton({ label, onClick, testId }: MenuButtonProps): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        padding: '7px 12px',
        background: hover ? 'var(--surface-2)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        outline: 'none',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
      }}
    >
      {label}
    </button>
  )
}
